import * as React from 'react';

import {
  Button,
  Divider,
  Grid,
  ListItem,
  Stack,
} from '@mui/material';

import LaunchIcon from '@mui/icons-material/Launch';

import { DateTime } from 'luxon';

import { DraggableAttributes, UniqueIdentifier } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

import { EventReport, ReportType } from '../../core/api/clients';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import useGetFormMetadataByReportType from '../../core/api/hooks/common/useGetFormMetadataByReportType';
import { INDIVIDUAL_REPORTS_COLLECTION_ID, useUnattachedEventReportsData } from '../../core/context/UnattachedEventReportsContext';

import FormAvatar from '../FormAvatar';
import NarrativeExpander from '../NarrativeExpander';
import CreateIncidentButton from './CreateIncidentButton';
import PropertyStack from '../PropertyStack';
import SortableEventReportActionButton from './SortableEventReportActionButton';
import ReportOrder from '../Enums/ReportOrder';

interface SortableEventReportProps {
  eventReport: EventReport;
  parentContainerId: UniqueIdentifier | null;
  draggableAttributes?: DraggableAttributes;
  draggableListeners?: SyntheticListenerMap | undefined;
}

function SortableEventReport({
  eventReport,
  parentContainerId,
  draggableAttributes,
  draggableListeners,
}: SortableEventReportProps) {
  const { isAdministrator } = useAuthorizedFormData();
  const { data: formMetadata } = useGetFormMetadataByReportType(eventReport?.reportType ?? ReportType.Incident);
  const isIndividualReport = parentContainerId === INDIVIDUAL_REPORTS_COLLECTION_ID;

  const handlePrimaryClick = () => {
    window.open(`${formMetadata?.route}/${eventReport.id}/view`, '_blank', 'width=1000, height=700')?.focus();
  };

  const getPropertiesForPrimaryText = (): Record<string, string | null | undefined> => {
    if (parentContainerId === INDIVIDUAL_REPORTS_COLLECTION_ID) {
      return {
        'Flight Number': eventReport.flightNumber,
        'City Pair': `${eventReport.departureAirport} - ${eventReport.scheduledArrivalAirport}`,
        'Landing Airport': eventReport.landingAirport,
        'Event Date': DateTime.fromISO(eventReport.eventDate ?? '').toLocaleString(DateTime.DATE_SHORT),
        'Aircraft Number': eventReport.aircraftNumber,
        'Created By': eventReport.employeeName,
      };
    }

    return {
      'Aircraft Number': eventReport.aircraftNumber,
      'Landing Airport': eventReport.landingAirport,
      'Sequence Number': `${eventReport.flightSequence}`,
      'Created By': eventReport.employeeName,
    };
  };

  return (
    <ListItem
      secondaryAction={((
        <SortableEventReportActionButton
          eventReportId={eventReport.id}
          parentContainerId={parentContainerId}
          draggableAttributes={draggableAttributes}
          draggableListeners={draggableListeners}
        />
        )
      )}
    >
      <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
        <Grid item xs={1}>
          <Stack direction="column" justifyContent="flex-start" alignItems="center" spacing={2}>
            <FormAvatar color={formMetadata?.color ?? 'grey'} title={eventReport.reportType} hasDecorator={eventReport.order === ReportOrder.FER_Captain} />
            <Button size="small" endIcon={<LaunchIcon />} onClick={handlePrimaryClick}>
              Open
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={9}>
          <Stack flexGrow={1} spacing={2}>
            <PropertyStack
              direction="row"
              spacing={2}
              divider={<Divider orientation="vertical" flexItem />}
              properties={getPropertiesForPrimaryText()}
            />
            <NarrativeExpander narrative={eventReport.narrative} />
          </Stack>
        </Grid>
        {isIndividualReport && isAdministrator && (
          <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <CreateIncidentButton eventReports={[eventReport]} />
          </Grid>
        )}
      </Grid>
    </ListItem>
  );
}

export default SortableEventReport;
